import { ITopLeagueFeedResponse } from "@finbackoffice/websocket-client";
import {
    createContext,
    FC,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { BrowserStorageIdsEnum, IBrowserStorageContext } from "./browser.storage.provider";

const HOUR_IN_MS = 60 * 60 * 1000;

export type IFavEvent = {
    id: number;
    sport_id: number;
    tournament_id: number;
    timestamp: number;
};

type ISportFavoritesContext = {
    favEvents: IFavEvent[];
    favLiveLeagues: ITopLeagueFeedResponse[];
    favPrematchLeagues: ITopLeagueFeedResponse[];
    favLiveLeaguesGameCount: number;
    favPrematchLeaguesGameCount: number;
    isFav: (id: number) => boolean;
    handleFavClick: (
        gameId: number,
        sportId: number,
        tournamentId: number,
        scheduledAt: string,
    ) => void;
};

export const SportFavoritesContext = createContext<ISportFavoritesContext>(null as any);

export type ISportFavoritesActionsContext = {
    handleFavClick: (
        gameId: number,
        sportId: number,
        tournamentId: number,
        scheduledAt: string,
    ) => void;
};

export const SportFavoritesActionsContext = createContext<ISportFavoritesActionsContext>(
    null as any,
);

type IFavoriteSocketStorage = {
    getEvent: (id: number) =>
        | {
              tournament_id: number;
              tournament_name: string;
              sport_slug: string;
              sport_id: number;
              sport_name: string;
              category_id: number;
              category_name: string;
          }
        | undefined;
    getTournamentById: (id: number) => { is_top?: boolean } | null | undefined;
};

type IProps = {
    children: ReactNode;
    socketStorage: IFavoriteSocketStorage | any;
    browserStorage: IBrowserStorageContext;
    liveFeedReady: boolean;
    prematchFeedReady: boolean;
};

export const SportFavoritesProvider: FC<IProps> = ({
    children,
    socketStorage,
    browserStorage,
    liveFeedReady,
    prematchFeedReady,
}) => {
    const [favEvents, setFavEvents] = useState<IFavEvent[]>([]);

    const favEventsRef = useRef<IFavEvent[]>([]);
    favEventsRef.current = favEvents;

    const browserStorageRef = useRef(browserStorage);
    browserStorageRef.current = browserStorage;

    const favEventIds = useMemo(() => new Set(favEvents.map((e) => e.id)), [favEvents]);
    const favEventIdsRef = useRef(favEventIds);
    favEventIdsRef.current = favEventIds;

    const favLiveLeagues = useMemo<ITopLeagueFeedResponse[]>(() => {
        if (!liveFeedReady) return [];
        const result: ITopLeagueFeedResponse[] = [];

        favEvents.forEach((event) => {
            const liveEvent = socketStorage.getEvent(event.id);
            if (liveEvent) {
                const isTop = socketStorage.getTournamentById(liveEvent.tournament_id)?.is_top;
                if (!isTop) {
                    const favLeague = result.find(
                        (league) => league.id === liveEvent.tournament_id,
                    );

                    if (!favLeague) {
                        result.push({
                            events: [event.id],
                            id: liveEvent.tournament_id,
                            games_count: 1,
                            name: liveEvent.tournament_name,
                            is_top: false,
                            top_index: 0,
                            sport_slug: liveEvent.sport_slug,
                            sport_id: liveEvent.sport_id,
                            sport_name: liveEvent.sport_name,
                            category_id: liveEvent.category_id,
                            category_name: liveEvent.category_name,
                        });
                    } else {
                        favLeague.events.push(event.id);
                        favLeague.games_count += 1;
                    }
                }
            }
        });

        return result;
    }, [favEvents, liveFeedReady, socketStorage]);

    const favPrematchLeagues = useMemo<ITopLeagueFeedResponse[]>(() => {
        if (!prematchFeedReady) return [];
        const result: ITopLeagueFeedResponse[] = [];

        favEvents.forEach((event) => {
            if (Date.now() < event.timestamp) {
                const favLeague = result.find((league) => league.id === event.tournament_id);

                if (!favLeague) {
                    result.push({
                        events: [event.id],
                        id: event.tournament_id,
                        games_count: 1,
                        name: "",
                        is_top: false,
                        top_index: 0,
                        sport_slug: "",
                        sport_id: event.sport_id,
                        sport_name: "",
                        category_id: -1,
                        category_name: "",
                    });
                } else {
                    favLeague.events.push(event.id);
                    favLeague.games_count += 1;
                }
            }
        });

        return result;
    }, [favEvents, prematchFeedReady]);

    const favLiveLeaguesGameCount = useMemo(
        () => favLiveLeagues.reduce((acc, league) => acc + league.games_count, 0),
        [favLiveLeagues],
    );

    const favPrematchLeaguesGameCount = useMemo(
        () => favEvents.filter((event) => Date.now() < event.timestamp).length,
        [favEvents],
    );

    useEffect(() => {
        const savedEvents: IFavEvent[] = browserStorage.get(BrowserStorageIdsEnum.FAVORITE_EVENTS);

        if (Array.isArray(savedEvents)) {
            const activeEvents = savedEvents.filter(
                (event) => Date.now() - event.timestamp < 4 * HOUR_IN_MS,
            );

            setFavEvents(activeEvents);
            browserStorage.set(BrowserStorageIdsEnum.FAVORITE_EVENTS, JSON.stringify(activeEvents));
        } else {
            browserStorage.set(BrowserStorageIdsEnum.FAVORITE_EVENTS, JSON.stringify([]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const addFav = useCallback((event: IFavEvent) => {
        const result = [...favEventsRef.current, event];
        setFavEvents(result);
        browserStorageRef.current.set(
            BrowserStorageIdsEnum.FAVORITE_EVENTS,
            JSON.stringify(result),
        );
    }, []);

    const removeFav = useCallback((id: number) => {
        const result = favEventsRef.current.filter((event) => event.id !== id);
        setFavEvents(result);
        browserStorageRef.current.set(
            BrowserStorageIdsEnum.FAVORITE_EVENTS,
            JSON.stringify(result),
        );
    }, []);

    const isFav = useCallback(
        (id: number) => {
            return favEventIds.has(id);
        },
        [favEventIds],
    );

    const handleFavClick = useCallback(
        (gameId: number, sportId: number, tournamentId: number, scheduledAt: string) => {
            if (favEventIdsRef.current.has(gameId)) {
                removeFav(gameId);
            } else {
                addFav({
                    id: gameId,
                    timestamp: scheduledAt ? new Date(scheduledAt).valueOf() : 0,
                    sport_id: sportId,
                    tournament_id: tournamentId,
                });
            }
        },
        [addFav, removeFav],
    );

    const value = useMemo(
        () => ({
            favEvents,
            favLiveLeagues,
            favPrematchLeagues,
            favLiveLeaguesGameCount,
            favPrematchLeaguesGameCount,
            isFav,
            handleFavClick,
        }),
        [
            favEvents,
            favLiveLeagues,
            favPrematchLeagues,
            favLiveLeaguesGameCount,
            favPrematchLeaguesGameCount,
            isFav,
            handleFavClick,
        ],
    );

    const actionsValue = useMemo(() => ({ handleFavClick }), [handleFavClick]);

    return (
        <SportFavoritesActionsContext.Provider value={actionsValue}>
            <SportFavoritesContext.Provider value={value}>
                {children}
            </SportFavoritesContext.Provider>
        </SportFavoritesActionsContext.Provider>
    );
};
